import { AUTH_BASE_URL, STIP_BASE_URL } from "../props";
import {
  useGet,
  usePut,
  usePost,
  useDelete,
  useUpload,
} from "../common/fetchServices";
import { find, propEq } from "ramda";

const getProgramsUrl = () => `${STIP_BASE_URL}/programs`;
const getProgramUrl = ({ id }) => `${STIP_BASE_URL}/programs/${id}`;

const getProjectsUrl = () => `${STIP_BASE_URL}/projects`;
const getProjectUrl = ({ id }) => `${STIP_BASE_URL}/projects/${id}`;

const getProjectOrgLogoUrl = ({ projectId, organizationId }) =>
  `${STIP_BASE_URL}/projects/${projectId}/organizations/${organizationId}/logo`;

const getProgramImagesUrl = ({ programKey }) =>
  `${STIP_BASE_URL}/csp-images/${programKey}`;
const getProgramImageUrl = ({ programKey, filename }) =>
  `${STIP_BASE_URL}/csp-images/${programKey}/${filename}`;

const getGenerateRipEndpointUrl = ({
  programKey,
  projectKey,
  creatorOrganizationId,
}) =>
  `${STIP_BASE_URL}/programs/${programKey}/projects/${projectKey}/rip-endpoint/${creatorOrganizationId}`;

const getUpdateOptInUrl = ({ id }) => `${STIP_BASE_URL}/optins/${id}`;

export const usePrograms = () => {
  const url = `${STIP_BASE_URL}/programs`;
  return useGet("programs", url);
};

export const useProgram = programId => {
  const url = `${STIP_BASE_URL}/programs/${programId}`;
  return useGet(["programs", { id: programId }], url);
};

export const useProgramUpdate = () => {
  return usePut(getProgramUrl, {
    queriesToInvalidate: ["programs"],
  });
};

export const useProgramCreate = () => {
  return usePost(getProgramsUrl, {
    queriesToInvalidate: ["programs"],
  });
};

export const useProjects = () => {
  const url = `${STIP_BASE_URL}/projects`;
  return useGet("projects", url);
};

export const useProjectsForProgram = programId => {
  const url = `${STIP_BASE_URL}/programs/${programId}/projects`;
  return useGet(["program-projects", { programId }], url);
};

export const useCategories = () => {
  const url = `${STIP_BASE_URL}/categories`;
  return useGet("categories", url);
};

export const useAudiences = () => {
  const url = `${STIP_BASE_URL}/audiences`;
  return useGet("audiences", url);
};

export const useOptIns = () => {
  const url = `${STIP_BASE_URL}/optins`;
  return useGet("opt-ins", url, true, true);
};

export const useSubmissionTypes = () => {
  const url = `${STIP_BASE_URL}/submission-types`;
  return useGet("submission-types", url);
};

export const useAudioTypes = () => {
  const url = `${STIP_BASE_URL}/audio-types`;
  return useGet("audio-types", url);
};

export const useVideoRatings = () => {
  const url = `${STIP_BASE_URL}/ratings`;
  return useGet("video-ratings", url);
};

export const useImageFormats = () => {
  const url = `${STIP_BASE_URL}/medium-formats/image`;
  return useGet("image-formats", url);
};

export const useDocumentFormats = () => {
  const url = `${STIP_BASE_URL}/medium-formats/other-document`;
  return useGet("document-formats", url);
};

export const useContributorRoles = () => {
  const url = `${STIP_BASE_URL}/contributors/roles`;
  return useGet("contributor-roles", url);
};

export const useLibraries = () => {
  const url = `${AUTH_BASE_URL}/roles/ROLE_ORGANIZATION/organizations`;
  return useGet("libraries", url);
};

export const useCountries = () => {
  const url = `${STIP_BASE_URL}/geography/countries`;
  return useGet("countries", url);
};

export const useRegionSearch = searchString => {
  const url = `${STIP_BASE_URL}/geography/search/subdivisions/${searchString}`;
  return useGet(["regions", { searchString }], url);
};

export const useRegion = code => {
  const url = `${STIP_BASE_URL}/geography/subdivisions/${code}`;
  return useGet(["regions", { code }], url);
};

export const useLibraryOptions = () => {
  const url = `${STIP_BASE_URL}/library-options`;
  return useGet("library-options", url);
};

export const useProject = id => {
  const url = `${STIP_BASE_URL}/projects/${id}`;
  return useGet(["projects", { id }], url);
};

export const useProjectUpdate = () => {
  return usePut(getProjectUrl, {
    queriesToInvalidate: ["program-projects", "projects"],
  });
};

export const useProjectCreate = () => {
  return usePost(getProjectsUrl, {
    queriesToInvalidate: ["projects", "program-projects"],
  });
};

export const useProjectDelete = () => {
  return useDelete(getProjectUrl, {
    queriesToInvalidate: ["projects", "program-projects"],
  });
};

export const useGenerateRipEndpoint = () => {
  return usePost(getGenerateRipEndpointUrl, {
    queriesToInvalidate: [["projects", ["id"]]],
  });
};

export const useLibraryOptionCreate = () => {
  return usePost(() => `${STIP_BASE_URL}/library-options`, {
    queriesToInvalidate: ["library-options"],
  });
};

export const useOrganizationLogos = projectId => {
  const url = `${STIP_BASE_URL}/projects/${projectId}/logos`;
  return useGet(["organization-logos", { projectId }], url);
};

export const useOrganizationLogoUpload = () => {
  return useUpload(getProjectOrgLogoUrl, {
    props: { paramName: "logo-file", method: "POST" },
    queriesToInvalidate: [["organization-logos", ["projectId"]]],
  });
};

export const useOrganizationLogoDelete = () => {
  return useDelete(getProjectOrgLogoUrl, {
    queriesToInvalidate: [["organization-logos", ["projectId"]]],
  });
};

export const useProgramImages = programKey => {
  const url = `${STIP_BASE_URL}/csp-images/${programKey}`;
  return useGet(["program-images", { programKey }], url);
};

export const useProgramImageUpload = () => {
  return useUpload(getProgramImagesUrl, {
    props: { paramName: "image-file", method: "POST" },
    queriesToInvalidate: [["program-images", ["programKey"]]],
  });
};

export const useProgramImageDelete = () => {
  return useDelete(getProgramImageUrl, {
    queriesToInvalidate: [["program-images", ["programKey"]]],
  });
};

export const useCreateOptIn = () => {
  return usePost(() => `${STIP_BASE_URL}/optins`, {
    queriesToInvalidate: ["opt-ins", "projects"],
  });
};

export const useUpdateOptIn = () => {
  return usePut(getUpdateOptInUrl, {
    queriesToInvalidate: ["opt-ins", "projects"],
  });
};

export const expandDisplayName = type => {
  if (type.name.endsWith("WITH_DOCX")) {
    return `${type.displayName} (with DocX)`;
  } else if (type.name.endsWith("PDF_ONLY")) {
    return `${type.displayName} (PDF Only)`;
  } else if (type.name.endsWith("EPUB_ONLY")) {
    return `${type.displayName} (EPUB Only)`;
  } else if (type.name.endsWith("PDF_AND_DOCX")) {
    return `${type.displayName} (PDF or DocX)`;
  } else {
    return type.displayName;
  }
};

export const SocialLinkPlatforms = [
  { label: "FaceBook", value: "facebook" },
  { label: "Instagram", value: "instagram" },
  { label: "Twitter", value: "twitter" },
  { label: "X", value: "x" },
  { label: "YouTube", value: "youtube" },
  { label: "Link", value: "link" },
];

export const getSocialLinkPlatform = value =>
  find(propEq("value", value), SocialLinkPlatforms);
